/**
 * components/SearchInput
 * The register's 210px × 30px search field. Shorter than the standard 33px
 * input because it sits in a header row beside 30px buttons and must align
 * with them.
 *
 * Filtering is client-side and immediate — the register is already loaded, so
 * there is nothing to debounce and nothing to wait for. The clear button
 * appears only once there is something to clear.
 */
import { useState } from 'react';
import { Pressable, StyleSheet, Text, TextInput, View } from 'react-native';
import { neutral } from '../../theme/colors';
import { radii } from '../../theme/radii';
import { controlHeight } from '../../theme/spacing';
import { sans, size as fontSize } from '../../theme/typography';
import { noFocusOutline } from '../../theme/webStyle';
import Icon from '../Icon';

export interface SearchInputProps {
  value: string;
  onChangeText: (value: string) => void;
  placeholder?: string;
  /** 210px in the register; full width when a screen stacks on a phone. */
  width?: number | '100%';
  /**
   * A leading magnifier. OFF by default because the design's field has none —
   * it is a bare 30px input with a placeholder. Kept as an option rather than
   * deleted, since a search field elsewhere may want one.
   */
  showIcon?: boolean;
}

export function SearchInput({
  value,
  onChangeText,
  placeholder = 'Search',
  width = 210,
  showIcon = false,
}: SearchInputProps) {
  const [focused, setFocused] = useState(false);

  return (
    <View
      style={[
        styles.shell,
        { width, borderColor: focused ? neutral.ink : neutral.border },
      ]}
    >
      {showIcon && <Icon name="search" size={13} color={neutral.grey400} strokeWidth={1.4} />}
      <TextInput
        value={value}
        onChangeText={onChangeText}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        placeholderTextColor={neutral.grey400}
        autoCorrect={false}
        autoCapitalize="none"
        style={styles.input}
      />
      {value.length > 0 && (
        <Pressable
          onPress={() => onChangeText('')}
          accessibilityRole="button"
          accessibilityLabel="Clear search"
          hitSlop={6}
        >
          <Text style={styles.clear}>×</Text>
        </Pressable>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  shell: {
    height: controlHeight.md,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    paddingHorizontal: 11,
    borderWidth: 1,
    borderRadius: radii.control,
    backgroundColor: neutral.surface,
  },
  input: {
    flex: 1,
    height: '100%',
    ...sans('400'),
    fontSize: fontSize.label,
    color: neutral.ink,
    padding: 0,
    ...noFocusOutline,
  },
  clear: { ...sans('400'), fontSize: 14, lineHeight: 15, color: neutral.grey400 },
});

export default SearchInput;
